import type { ReactNode } from "react";
import {
  Button as RACButton,
  DropZone as RACDropZone,
  FileTrigger as RACFileTrigger,
  isFileDropItem,
  type DropZoneProps as RACDropZoneProps,
} from "react-aria-components";

/** Not a named export of `react-aria-components` — pulled off `onDrop`'s own
 *  parameter type rather than reaching into `@react-types/shared`. */
type DropEvent = Parameters<NonNullable<RACDropZoneProps["onDrop"]>>[0];

import styles from "./dropzone.module.css";

export interface DropzoneProps {
  /**
   * Fires with every file the user hands over, by click-to-browse or by
   * drag-and-drop — both paths converge here rather than callers wiring two
   * handlers. `File[]`, not `FileList`, since a drop never produces one.
   */
  onSelect: (files: File[]) => void;
  /** Forwarded to the underlying `<input type="file">`'s `accept`. Not
   *  applied to the drop path — nothing in `react-aria-components` filters a
   *  drop by mime type, and neither did the two hand-rolls this retires.
   *
   *  TODO: the click path filters by `accept` and the drop path does not, so
   *  the same restriction is enforced or ignored depending on how the user
   *  supplies the file. Deliberately matches the retired hand-rolls' existing
   *  behaviour rather than changing it inside a migration (SR-301 phase 8,
   *  unit 2a, mandate #6). Closing it means filtering in `onSelect` against
   *  `acceptedFileTypes` before calling the caller's handler. */
  acceptedFileTypes?: ReadonlyArray<string>;
  isDisabled?: boolean;
  /**
   * The dropzone's visible content (icon, prompt, hint) — callers own copy
   * and localization, same as `file-trigger.tsx`'s `button` bag owns its own
   * label.
   */
  children: ReactNode;
  "data-test-role"?: string;
}

/**
 * A click-to-browse + drag-and-drop file target. Retires the two
 * byte-identical hand-rolled dropzones (SR-301 phase 8 doc §4e, G-11) —
 * `items.tsx`/`items.module.css` and `locations.tsx`/`locations.module.css`
 * — which duplicated `isDragOver` state, manual `onDragOver`/`onDragLeave`/
 * `onDrop` handlers, and a hidden `<input type="file">` behind a `<label>`.
 *
 * Built as a sibling to `FileTrigger`, not an arm on it: `file-trigger.tsx`
 * has zero call sites repo-wide (nothing to preserve compatibility with,
 * per the phase 8 doc), and RAC ships a real `DropZone` — pairing it with
 * `FileTrigger` for the click path, rather than teaching `FileTrigger` to be
 * a drop target too, is the documented RAC recipe and keeps each component's
 * job single.
 */
function Dropzone({
  onSelect,
  acceptedFileTypes,
  isDisabled,
  children,
  "data-test-role": testRole,
}: DropzoneProps) {
  const onDrop = async (event: DropEvent) => {
    const files = await Promise.all(
      event.items.filter(isFileDropItem).map((item) => item.getFile()),
    );
    if (files.length > 0) onSelect(files);
  };

  return (
    <RACDropZone
      className={styles.dropzone}
      isDisabled={isDisabled}
      onDrop={onDrop}
      data-test-role={testRole}
    >
      <RACFileTrigger
        acceptedFileTypes={acceptedFileTypes}
        onSelect={(fileList) => {
          if (fileList) onSelect(Array.from(fileList));
        }}
      >
        <RACButton className={styles.trigger} isDisabled={isDisabled}>
          {children}
        </RACButton>
      </RACFileTrigger>
    </RACDropZone>
  );
}

export default Dropzone;
